import lang from '@/lang'
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

import request from '@/modules/base/util/request'
import config from '@/config'
import { toast } from '@/modules/base/components/toast/toastConfig'

const url = `${config.resourceHost}ai/chat/conversations`
export const messageFeedbackUrl = (messageId) => `${config.resourceHost}ai/chat/messages/${messageId}/feedback`

export type ChatMessageRole = 'user' | 'assistant' | 'system'
export const ChatMessageRole = {
  USER: 'user',
  ASSISTANT: 'assistant',
  SYSTEM: 'system',
} as const

export type FeedbackRating = 'up' | 'down'
export const FeedbackRating = {
  UP: 'up',
  DOWN: 'down',
} as const

export interface FeedbackData {
  rating: FeedbackRating
  tags: string[]
  comment: string
}
export interface ChatMessage {
  id: string
  content: string
  role: ChatMessageRole
  createdAt?: number
  updatedAt?: number
  feedback?: FeedbackData
}

export type ConversationStatus = 'pending' | 'completed' | 'failed'
export const ConversationStatus = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const
export interface Conversation {
  id: string
  title: string
  messages: ChatMessage[],
  createdAt?: number
  updatedAt?: number
  status: ConversationStatus
  isNew?: boolean
}

export interface PopperSize {
  width: number
  height: number
}

export interface PopperPosition {
  x: number
  y: number
}

export interface ButtonPosition {
  x: number
  y: number
}

export type IAiChatView = 'chat' | 'conversationList' | 'settings'
export const AiChatView = {
  CHAT: 'chat',
  CONVERSATION_LIST: 'conversationList',
  SETTINGS: 'settings',
} as const

export type SendMessageOption = 'enter' | 'enter-ctrl'
export const SendMessageOption = {
  ENTER: 'enter',
  ENTER_CTRL: 'enter-ctrl',
} as const

export interface ChatState {
  conversations: Conversation[]
  currentConversationId: string | null
  inputValue: string
  attachments: File[]
  isLoading: boolean
  hasMoreConversations: boolean
  unreadConversationIds: string[]
  sendMessageOption: SendMessageOption | null
  sendAfterMicInput: boolean
  popperSize: PopperSize | null
  popperOpen: boolean
  popperPosition: PopperPosition | null
  aiChatView: IAiChatView
  buttonPosition: ButtonPosition | null
}

const STORAGE_KEY_SIZE = 'ai-chat-popper-size'
const STORAGE_KEY_BUTTON_POSITION = 'ai-chat-button-position'
const STORAGE_KEY_POPPER_POSITION = 'ai-chat-popper-position'
const STORAGE_KEY_SEND_MESSAGE_OPTION = 'ai-chat-send-message-option'
const STORAGE_KEY_SEND_AFTER_MIC_INPUT = 'ai-chat-send-after-mic-input'

const saveToLocalStorage = <T>(key: string, value: T) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error('Failed to save to localStorage:', error)
  }
}

const loadFromLocalStorage = <T>(key: string): T | null => {
  try {
    const saved = localStorage.getItem(key)
    return saved ? JSON.parse(saved) as T : null
  } catch {
    return null
  }
}

const initialState: ChatState = {
  conversations: [],
  currentConversationId: null,
  inputValue: '',
  attachments: [],
  isLoading: false,
  hasMoreConversations: false,
  unreadConversationIds: [],
  sendMessageOption: loadFromLocalStorage<SendMessageOption>(STORAGE_KEY_SEND_MESSAGE_OPTION) || SendMessageOption.ENTER,
  sendAfterMicInput: loadFromLocalStorage<boolean>(STORAGE_KEY_SEND_AFTER_MIC_INPUT) || false,
  aiChatView: AiChatView.CHAT,
  popperSize: loadFromLocalStorage<PopperSize>(STORAGE_KEY_SIZE),
  popperOpen: false,
  popperPosition: loadFromLocalStorage<PopperPosition>(STORAGE_KEY_POPPER_POSITION),
  buttonPosition: loadFromLocalStorage<ButtonPosition>(STORAGE_KEY_BUTTON_POSITION),
}

export const sendMessage = createAsyncThunk<
  { status: ConversationStatus, title: string, isNew: boolean, conversationId: string, message: string },
  { conversationId: string | null, message: string, files?: File[] },
  { rejectValue: string }
>(
  'ai/chat/sendMessage',
  async({ conversationId, message }: { conversationId: string | null, message: string, files?: File[] }, { rejectWithValue }) => {
    try {
      const sendMessageUrl = conversationId ? `${url}/${conversationId}` : `${url}`
      const response = await request(sendMessageUrl, {
        method: 'POST',
        auth: true,
        data: { text: message },
      })

      const { status, conversationId: newConversationId, title } = response.body.data
      return { status, title, isNew: !conversationId, conversationId: conversationId || newConversationId, message }
    } catch (error) {
      console.error('Error sending message:', error)
      return rejectWithValue(lang.ai.chat.errors.message.send)
    }
  },
)

export const getConversation = createAsyncThunk<
  Conversation,
  { conversationId: string },
  { rejectValue: string }
>(
  'ai/chat/getConversation',
  async({ conversationId }: { conversationId: string }, { rejectWithValue }) => {
    try {
      const response = await request(`${url}/${conversationId}`, {
        method: 'GET',
        auth: true,
      })

      return response.body.data.conversation
    } catch (error) {
      console.error('Error getting conversation:', error)
      return rejectWithValue(lang.ai.chat.errors.conversation.get)
    }
  },
)

export const getConversations = createAsyncThunk<
  { conversations: Conversation[], count: number },
  { limit: number, offset: number },
  { rejectValue: string }
>(
  'ai/chat/getConversations',
  async({ limit, offset }: { limit: number, offset: number }, { rejectWithValue }) => {
    try {
      const response = await request(`${url}`, {
        method: 'GET',
        auth: true,
        data: { limit, offset },
      })

      return { conversations: response.body.data.conversations, count: response.body.data.count }
    } catch (error) {
      console.error('Error getting conversation:', error)
      return rejectWithValue(lang.ai.chat.errors.conversations.get)
    }
  },
)

export const getMessages = createAsyncThunk<
  { messages: ChatMessage[], conversationId: string },
  { conversationId: string },
  { rejectValue: string }
>(
  'ai/chat/getMessages',
  async({ conversationId }: { conversationId: string }, { rejectWithValue }) => {
    try {
      const response = await request(`${url}/${conversationId}/messages`, {
        method: 'GET',
        auth: true,
      })

      return { messages: response.body.data.messages, conversationId }
    } catch (error) {
      console.error('Error getting message:', error)
      return rejectWithValue(lang.ai.chat.errors.messages.get)
    }
  },
)

export const getMessage = createAsyncThunk<
  { message: ChatMessage, conversationId: string },
  { messageId: string, conversationId: string },
  { rejectValue: string }
>(
  'ai/chat/getMessage',
  async({ messageId, conversationId }: { messageId: string, conversationId: string }, { rejectWithValue }) => {
    try {
      const response = await request(`${url}/${conversationId}/messages/${messageId}`, {
        method: 'GET',
        auth: true,
      })

      return { message: response.body.data.message, conversationId }
    } catch (error) {
      console.error('Error getting message:', error)
      return rejectWithValue(lang.ai.chat.errors.message.get)
    }
  },
)

export const deleteConversation = createAsyncThunk<
  { conversationId: string },
  { conversationId: string },
  { rejectValue: string }
>(
  'ai/chat/deleteConversation',
  async({ conversationId }: { conversationId: string }, { rejectWithValue }) => {
    try {
      await request(`${url}/${conversationId}`, {
        method: 'DELETE',
        auth: true,
      })

      return { conversationId }
    } catch (error) {
      console.error('Error deleting conversation:', error)
      return rejectWithValue(lang.ai.chat.errors.conversation.delete)
    }
  },
)

const chatSlice = createSlice({
  name: 'ai/chat',
  initialState,
  reducers: {
    setPopperOpenState: (state, action: PayloadAction<boolean>) => {
      state.popperOpen = action.payload
    },
    setInputValue: (state, action: PayloadAction<string>) => {
      state.inputValue = action.payload
    },
    setAttachments: (state, action: PayloadAction<File[]>) => {
      state.attachments = action.payload
    },
    selectConversation: (state, action: PayloadAction<string>) => {
      state.currentConversationId = action.payload
      state.inputValue = ''

      const conversationId = action.payload
      const index = state.unreadConversationIds.indexOf(conversationId)
      if (index !== -1) {
        state.unreadConversationIds.splice(index, 1)
      }
    },
    createNewConversation: (state) => {
      state.currentConversationId = null
      state.inputValue = ''
    },
    clearChat: (state) => {
      if (state.currentConversationId) {
        const conversation = state.conversations.find(t => t.id === state.currentConversationId)
        if (conversation) {
          conversation.messages = []
        }
      }
      state.inputValue = ''
      state.isLoading = false
    },
    addUnreadConversationId: (state, action: PayloadAction<string>) => {
      if (state.unreadConversationIds.includes(action.payload)) {
        return
      }
      state.unreadConversationIds.push(action.payload)
    },
    markConversationAsRead: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload
      const index = state.unreadConversationIds.indexOf(conversationId)
      if (index !== -1) {
        state.unreadConversationIds.splice(index, 1)
      }
    },
    markAllConversationsAsRead: (state) => {
      state.unreadConversationIds = []
    },
    setPopperSize: (state, action: PayloadAction<PopperSize>) => {
      state.popperSize = action.payload
      saveToLocalStorage<PopperSize>(STORAGE_KEY_SIZE, action.payload)
    },
    setAiChatView: (state, action: PayloadAction<IAiChatView>) => {
      state.aiChatView = action.payload
    },
    setButtonPosition: (state, action: PayloadAction<ButtonPosition>) => {
      state.buttonPosition = action.payload
      saveToLocalStorage<ButtonPosition>(STORAGE_KEY_BUTTON_POSITION, action.payload)
    },
    resetButtonPosition: (state) => {
      state.buttonPosition = null
      localStorage.removeItem(STORAGE_KEY_BUTTON_POSITION)
    },
    setSendMessageOption: (state, action: PayloadAction<'enter' | 'enter-ctrl'>) => {
      state.sendMessageOption = action.payload
      saveToLocalStorage<SendMessageOption>(STORAGE_KEY_SEND_MESSAGE_OPTION, action.payload)
    },
    setSendAfterMicInput: (state, action: PayloadAction<boolean>) => {
      state.sendAfterMicInput = action.payload
      saveToLocalStorage<boolean>(STORAGE_KEY_SEND_AFTER_MIC_INPUT, action.payload)
    },
    setPopperPosition: (state, action: PayloadAction<PopperPosition>) => {
      state.popperPosition = action.payload
      saveToLocalStorage<PopperPosition>(STORAGE_KEY_POPPER_POSITION, action.payload)
    },
    setConversationStatus: (state, action: PayloadAction<{ conversationId: string, status: ConversationStatus }>) => {
      const { conversationId, status } = action.payload
      const conversationIndex = state.conversations.findIndex(t => t.id === conversationId)
      if (conversationIndex !== -1) {
        state.conversations[conversationIndex].status = status
      }
    },
    updateConversation: (state, action: PayloadAction<{ isNew: boolean }>) => {
      const { isNew } = action.payload
      const conversationIndex = state.conversations.findIndex(t => t.id === state.currentConversationId)
      if (conversationIndex !== -1) {
        state.conversations[conversationIndex].isNew = isNew
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true
      })
      .addCase(sendMessage.fulfilled, (state, action: PayloadAction<{ status: ConversationStatus, title: string, isNew: boolean, conversationId: string, message: string }>) => {
        state.isLoading = false
        const { status, title, isNew, conversationId, message } = action.payload

        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          content: message,
          role: 'user',
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }

        if (isNew) {
          const conversation: Conversation = {
            id: conversationId,
            title,
            status,
            messages: [userMessage],
            isNew: true,
          }
          state.conversations.unshift(conversation)
          state.currentConversationId = conversationId
        } else {
          const conversationIndex = state.conversations.findIndex(t => t.id === conversationId)
          if (conversationIndex !== -1) {
            state.conversations[conversationIndex].status = status
            state.conversations[conversationIndex].messages.push(userMessage)
          }
        }
        state.inputValue = ''
      })
      .addCase(sendMessage.rejected, (state) => {
        state.isLoading = false
        const errorMessage: ChatMessage = {
          id: `ai-error-${Date.now()}`,
          content: lang.ai.chat.errors.default,
          role: 'assistant',
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }

        if (state.currentConversationId) {
          const conversation = state.conversations.find(t => t.id === state.currentConversationId)
          if (conversation) {
            conversation.messages.push(errorMessage)
          }
        }
      })
      .addCase(getConversation.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getConversation.fulfilled, (state, action: PayloadAction<Conversation>) => {
        state.isLoading = false
        const conversationIndex = state.conversations.findIndex(t => t.id === action.payload.id)
        if (conversationIndex !== -1) {
          state.conversations[conversationIndex] = action.payload
        }
      })
      .addCase(getConversation.rejected, (state, action) => {
        state.isLoading = false
        toast.showToast({ type: 'error', message: action.payload || lang.ai.chat.errors.default })
      })
      .addCase(getConversations.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getConversations.fulfilled, (state, action: PayloadAction<{ conversations: Conversation[], count: number }>) => {
        state.isLoading = false
        state.conversations.push(...action.payload.conversations)
        state.hasMoreConversations = state.conversations.length < action.payload.count
      })
      .addCase(getConversations.rejected, (state, action) => {
        state.isLoading = false
        toast.showToast({ type: 'error', message: action.payload || lang.ai.chat.errors.default })
      })
      .addCase(getMessages.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getMessages.fulfilled, (state, action: PayloadAction<{ messages: ChatMessage[], conversationId: string }>) => {
        state.isLoading = false
        const conversationIndex = state.conversations.findIndex(t => t.id === action.payload.conversationId)
        if (conversationIndex !== -1) {
          state.conversations[conversationIndex].messages = action.payload.messages
        }
      })
      .addCase(getMessages.rejected, (state, action) => {
        state.isLoading = false
        toast.showToast({ type: 'error', message: action.payload || lang.ai.chat.errors.default })
      })
      // getMessage should NOT set global isLoading to avoid losing input focus
      // when receiving messages for other conversations
      .addCase(getMessage.fulfilled, (state, action: PayloadAction<{ message: ChatMessage, conversationId: string }>) => {
        const conversationIndex = state.conversations.findIndex(t => t.id === action.payload.conversationId)
        if (conversationIndex !== -1) {
          if (state.conversations[conversationIndex].messages.find(m => m.id === action.payload.message.id)) {
            return
          }
          state.conversations[conversationIndex].messages.push(action.payload.message)
        }
      })
      .addCase(getMessage.rejected, (state, action) => {
        toast.showToast({ type: 'error', message: action.payload || lang.ai.chat.errors.default })
      })
      .addCase(deleteConversation.pending, (state) => {
        state.isLoading = true
      })
      .addCase(deleteConversation.fulfilled, (state, action: PayloadAction<{ conversationId: string }>) => {
        state.isLoading = false
        const conversationIndex = state.conversations.findIndex(t => t.id === action.payload.conversationId)
        if (conversationIndex !== -1) {
          state.conversations.splice(conversationIndex, 1)
        }
        if (state.currentConversationId === action.payload.conversationId) {
          state.currentConversationId = null
        }
        toast.showToast({ type: 'success', message: lang.ai.chat.toasts.conversation.deleted })
      })
      .addCase(deleteConversation.rejected, (state, action) => {
        state.isLoading = false
        toast.showToast({ type: 'error', message: action.payload || lang.ai.chat.errors.default })
      })

  },
})

export const {
  setPopperOpenState,
  setInputValue,
  setAttachments,
  selectConversation,
  createNewConversation,
  setConversationStatus,
  clearChat,
  addUnreadConversationId,
  markConversationAsRead,
  markAllConversationsAsRead,
  setPopperSize,
  setAiChatView,
  setButtonPosition,
  resetButtonPosition,
  setSendMessageOption,
  setSendAfterMicInput,
  setPopperPosition,
  updateConversation,
} = chatSlice.actions

export const selectPopperOpen = (state: { ai: { chat: ChatState } }) => state.ai.chat.popperOpen
export const selectConversations = (state: { ai: { chat: ChatState } }) => state.ai.chat.conversations
export const selectCurrentConversationId = (state: { ai: { chat: ChatState } }) => state.ai.chat.currentConversationId
export const selectCurrentConversation = (state: { ai: { chat: ChatState } }) => {
  const { conversations, currentConversationId } = state.ai.chat
  return currentConversationId ? conversations.find(t => t.id === currentConversationId) : null
}
export const selectMessages = (state: { ai: { chat: ChatState } }) => {
  const currentConversation = selectCurrentConversation(state)
  return currentConversation ? currentConversation.messages : []
}
export const selectInputValue = (state: { ai: { chat: ChatState } }) => state.ai.chat.inputValue
export const selectIsLoading = (state: { ai: { chat: ChatState } }) => state.ai.chat.isLoading
export const selectHasMoreConversations = (state: { ai: { chat: ChatState } }) => state.ai.chat.hasMoreConversations
export const selectUnreadConversationIds = (state: { ai: { chat: ChatState } }) => state.ai.chat.unreadConversationIds
export const selectFirstUnreadConversationId = (state: { ai: { chat: ChatState } }) => {
  const { conversations, unreadConversationIds } = state.ai.chat
  const unreadConversation = conversations.find(t => unreadConversationIds.includes(t.id))
  return unreadConversation?.id || null
}
export const selectPopperSize = (state: { ai: { chat: ChatState } }) => state.ai.chat.popperSize
export const selectAiChatView = (state: { ai: { chat: ChatState } }) => state.ai.chat.aiChatView
export const selectButtonPosition = (state: { ai: { chat: ChatState } }) => state.ai.chat.buttonPosition
export const selectPopperPosition = (state: { ai: { chat: ChatState } }) => state.ai.chat.popperPosition
export const selectSendMessageOption = (state: { ai: { chat: ChatState } }) => state.ai.chat.sendMessageOption
export const selectSendAfterMicInput = (state: { ai: { chat: ChatState } }) => state.ai.chat.sendAfterMicInput
export const selectAttachments = (state: { ai: { chat: ChatState } }) => state.ai.chat.attachments

export default chatSlice.reducer
